/* eslint-disable no-console */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { debounce } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { Cart, CartItem, PaymentOption } from 'src/models/cart/_types';
import { fetchCart as fetchCartRecord, getIntent } from 'src/models/cart/cart';
import { hydrateCartItems } from 'src/models/cart/utils';
import { fetchClient } from 'src/models/client/client';
import { fetchGroup } from 'src/models/group/group';
import { fetchPackage } from 'src/models/package/package';
import { getPackageDefaultDates } from 'src/models/package/packageDatePresets';
import { PaymentMethod } from 'src/models/payment-methods/_types';
import { fetchPaymentMethods } from 'src/models/payment-methods/payment-methods';
import { fetchProduct, fetchProductVariant } from 'src/models/product/product';
import { fetchTrainerByTrainerId } from 'src/models/trainer/trainer';
import { User } from 'src/models/user/_types';
import { fetchUser } from 'src/models/user/user';
import { fetchWorkoutPlan } from 'src/models/workout-plan/workout-plan';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';
import { useListSearchParams } from 'src/utils/useListSearchParams';

interface PaymentDialogContextType {
  currentStep: PaymentStep;
  setCurrentStep: React.Dispatch<React.SetStateAction<PaymentStep>>;
  nextStep: () => void;
  prevStep: () => void;
  goToSelectUser: () => void;
  selectableItemTypes: string[];
  selectedItemType: string | null;
  setSelectedItemType: (type: string | null) => void;
  searchValue: string;
  debouncedSearchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  cart: Cart;
  setCart: React.Dispatch<React.SetStateAction<Cart>>;
  savedCart: Cart | null;
  setSavedCart: React.Dispatch<React.SetStateAction<Cart | null>>;
  user?: User;
  selectedUser?: User;
  setSelectedUser: React.Dispatch<React.SetStateAction<User | undefined>>;
  itemToEdit: CartItem | null;
  setItemToEdit: React.Dispatch<React.SetStateAction<CartItem | null>>;
  cartError: string | null;
  setCartError: React.Dispatch<React.SetStateAction<string | null>>;
  reset: () => void;
  fetchCartIsFetching: boolean;
  fetchCartIsLoading: boolean;
  fetchCartError: boolean;
  resetSelectedItemType: () => void;

  addToCart: (item: any, options?: any) => Promise<void>;
  removeFromCart: (item: CartItem, index?: number) => void;
  handleEditItem: (item: CartItem) => void;
  updateCartItem: (newItem: CartItem) => void;
  handleSaveItemSettings: () => void;
  setSelectedPaymentMethod: (paymentMethod: string | PaymentMethod) => void;
  setCheckNumber: (checkNumber: string) => void;
  updateLocationId: (locationId: Identifier) => void;
  toggleAutomaticTax: () => void;
  isRetryHydrating: boolean;
}

const isCheckPaymentMethod = (
  paymentMethod: string | PaymentMethod | undefined
): boolean => {
  if (!paymentMethod) return false;
  if (typeof paymentMethod === 'string') return paymentMethod === 'check';
  return paymentMethod.id === 'check' || paymentMethod.type === 'check';
};

export enum PaymentStep {
  SelectUser,
  SelectType,
  SelectItems,
  Bag,
  EditItem,
  SelectPayment,
  Confirmation,
  PaymentComplete,
}

const PaymentDialogContext = createContext<
  PaymentDialogContextType | undefined
>(undefined);

const usePaymentDialogContext = () => {
  const context = useContext(PaymentDialogContext);
  if (!context) {
    throw new Error(
      'usePaymentDialogContext must be used within a PaymentDialogProvider'
    );
  }
  return context;
};

const selectableItemTypes = [
  'package',
  'group',
  'plan',
  'product',
  'onlineTraining',
  'staffSubscription',
];

interface PaymentDialogProviderProps {
  children: React.ReactNode;
  user?: User;
  itemType?: string;
  initialFilterParams?: Record<string, any>;
  open?: boolean;
  retryFromCartId?: Identifier | null;
  retryCartUserId?: Identifier;
  onClose?: () => void;
}

const PaymentDialogProvider: React.FC<PaymentDialogProviderProps> = ({
  children,
  user,
  itemType,
  initialFilterParams,
  open = false,
  retryFromCartId = null,
  retryCartUserId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { session, user: currentUser } = useSession();
  const {
    filterParams,
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    paramPrefix: 'p_',
  });

  const [selectedUser, setSelectedUser] = useState<User | undefined>(user);

  const [isRetryHydrating, setIsRetryHydrating] = useState(false);

  // Apply initial filter params when provided
  // Use a ref to track if we've already applied these filters to avoid reapplying on re-renders
  const appliedInitialFiltersRef = React.useRef<string | null>(null);

  // Apply initial filters only after user is set to avoid interfering with cart initialization
  useEffect(() => {
    if (retryFromCartId) {
      return;
    }

    // Only apply filters if user is set (component is initialized)
    if (!selectedUser && user) {
      return; // Wait for selectedUser to be set
    }

    if (initialFilterParams) {
      // Create a stable key from the filter params to detect if they've changed
      const filterKey = JSON.stringify(initialFilterParams);

      // Only apply if these are new filters (different from what we've already applied)
      if (appliedInitialFiltersRef.current !== filterKey) {
        Object.entries(initialFilterParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            updateFilterParam(key, value);
          }
        });
        appliedInitialFiltersRef.current = filterKey;
      }
    } else {
      // Reset the ref when no initial filters are provided
      appliedInitialFiltersRef.current = null;
    }
  }, [initialFilterParams, selectedUser, user, updateFilterParam, retryFromCartId]);

  const [cart, setCart] = useState<Cart>(() => ({
    userId: selectedUser?.id || '',
    locationId: selectedUser?.locationId || '',
    cartItems: [],
    automaticTax: true,
  }));

  const [savedCart, setSavedCart] = useState<Cart | null>(null);

  const [cartError, setCartError] = useState<string | null>(null);

  const [currentStep, _setCurrentStep] = useState<PaymentStep>(
    selectedUser ? PaymentStep.SelectType : PaymentStep.SelectUser
  );

  const setCurrentStep = useCallback(
    (step: PaymentStep | ((prev: PaymentStep) => PaymentStep)) => {
      // Clear error when going back to payment method selection from confirmation
      if (typeof step === 'function') {
        _setCurrentStep((prev) => {
          const nextStep = step(prev);
          if (
            nextStep === PaymentStep.SelectPayment &&
            prev === PaymentStep.Confirmation
          ) {
            setCartError(null);
          }
          return nextStep;
        });
      } else {
        _setCurrentStep((prev) => {
          if (
            step === PaymentStep.SelectPayment &&
            prev === PaymentStep.Confirmation
          ) {
            setCartError(null);
          }
          return step;
        });
      }
    },
    [setCartError]
  );

  useLayoutEffect(() => {
    if (open && retryFromCartId && retryCartUserId) {
      setIsRetryHydrating(true);
    }
  }, [open, retryFromCartId, retryCartUserId]);

  useEffect(() => {
    if (!open) {
      setIsRetryHydrating(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !retryFromCartId || !retryCartUserId) {
      return;
    }

    let cancelled = false;

    (async () => {
      setIsRetryHydrating(true);
      try {
        const selectedUserData = await fetchUser({ id: retryCartUserId });
        if (cancelled) {
          return;
        }

        const loadedCart = await fetchCartRecord({
          id: retryFromCartId,
          userId: retryCartUserId,
        });
        if (cancelled) {
          return;
        }

        const hydratedItems = await hydrateCartItems(loadedCart, {
          session,
          currentUserId: currentUser?.id,
        });
        if (cancelled) {
          return;
        }

        if (!hydratedItems.length) {
          toast.error('Could not restore cart items.');
          onClose?.();
          return;
        }

        setSelectedUser(selectedUserData);
        setCart({
          userId: loadedCart.userId,
          platformId: loadedCart.platformId,
          locationId:
            loadedCart.locationId ?? selectedUserData.locationId ?? '',
          automaticTax: loadedCart.automaticTax ?? true,
          cartItems: hydratedItems,
          currency: loadedCart.currency,
          guestCheckout: loadedCart.guestCheckout,
          guestEmail: loadedCart.guestEmail,
          guestFirstName: loadedCart.guestFirstName,
          guestLastName: loadedCart.guestLastName,
        });
        setSavedCart(null);
        setCartError(null);
        setCurrentStep(PaymentStep.Bag);
      } catch (error: any) {
        if (!cancelled) {
          const detail = error?.response?.data?.errors?.[0]?.detail;
          toast.error(
            typeof detail === 'string'
              ? detail
              : 'Unable to load cart for retry.'
          );
          onClose?.();
        }
      } finally {
        if (!cancelled) {
          setIsRetryHydrating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    retryFromCartId,
    retryCartUserId,
    session,
    currentUser?.id,
    onClose,
    setCurrentStep,
  ]);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  const initialItemType = useMemo(() => {
    return selectableItemTypes.find((t) => t === itemType) || null;
  }, [itemType]);

  const [selectedItemType, _setSelectedItemType] = useState<string | null>(
    initialItemType
  );

  const [itemToEdit, setItemToEdit] = useState<CartItem | null>(null);

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setDebouncedSearchValue(value), 300),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchValue);
    return () => {
      debouncedSetSearch.clear();
    };
  }, [searchValue, debouncedSetSearch]);

  const setSelectedItemType = useCallback(
    (type: string | null) => {
      resetFilterParams();
      _setSelectedItemType(type);
    },
    [resetFilterParams]
  );

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      const nextStepValue = prev + 1;
      return nextStepValue in PaymentStep ? nextStepValue : prev;
    });
  }, [setCurrentStep]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => {
      const prevStepValue = prev - 1;

      if (itemToEdit) {
        setItemToEdit(null);
      }

      if (prevStepValue === PaymentStep.EditItem) {
        return PaymentStep.Bag;
      }

      return prevStepValue in PaymentStep ? prevStepValue : prev;
    });
  }, [itemToEdit, setCurrentStep]);

  const resetSelectedItemType = useCallback(() => {
    setSelectedItemType(initialItemType);
  }, [initialItemType, setSelectedItemType]);

  const goToSelectUser = useCallback(() => {
    // Clear cart when going back to select user
    setCart({ userId: '', cartItems: [] });
    setSelectedUser(undefined);
    resetSelectedItemType();
    setCurrentStep(PaymentStep.SelectUser);
  }, [setCurrentStep, resetSelectedItemType]);

  const reset = useCallback(() => {
    setSearchValue('');
    setDebouncedSearchValue('');
    debouncedSetSearch.clear(); // Clear any pending debounced search calls
    setItemToEdit(null);
    setSelectedUser(user); // Reset to the original user prop
    setCart({ userId: user?.id || '', cartItems: [] });
    setSavedCart(null);
    setCartError(null);
    resetSelectedItemType();
    resetFilterParams(); // Reset search/filter params
    appliedInitialFiltersRef.current = null; // Reset the applied filters ref

    // Set initial step based on whether user was provided
    if (user) {
      setCurrentStep(PaymentStep.SelectType);
    } else {
      setCurrentStep(PaymentStep.SelectUser);
    }

    queryClient.invalidateQueries({
      queryKey: ['charges'],
    });
    queryClient.invalidateQueries({
      queryKey: ['stripeSubscriptions'],
    });
    queryClient.invalidateQueries({
      queryKey: ['subscriptions'],
    });
    queryClient.invalidateQueries({
      queryKey: ['user-packages'],
    });
  }, [
    user,
    resetSelectedItemType,
    resetFilterParams,
    queryClient,
    setCurrentStep,
    debouncedSetSearch,
  ]);

  const refreshPageRetryRef = React.useRef(0);

  const {
    mutate: fetchCart,
    isPending: fetchCartIsFetching,
    isError: fetchCartError,
  } = useMutation({
    mutationFn: () => getIntent(cart),
    onSuccess: (data) => setSavedCart(data),
    onError: (error: any) => {
      if (error.response?.data && Array.isArray(error.response.data.errors)) {
        const errorMessage =
          error.response.data.errors[0]?.detail || 'An error occurred.';

        if (errorMessage === 'REFRESH_PAGE') {
          if (refreshPageRetryRef.current < 1) {
            refreshPageRetryRef.current += 1;
            getIntent(cart)
              .then((data) => setSavedCart(data))
              .catch(() => {
                setCartError(
                  'An error occurred while processing your payment. Please go back and try again.'
                );
              });
            return;
          }
          setCartError(
            'An error occurred while processing your payment. Please go back and try again.'
          );
          return;
        }

        setCartError(errorMessage);
      } else {
        setCartError('An error occurred.');
      }
    },
  });
  const fetchCartIsLoading = fetchCartIsFetching;

  useEffect(() => {
    if (currentStep === PaymentStep.Confirmation) {
      refreshPageRetryRef.current = 0;
      fetchCart();
    }
  }, [currentStep, fetchCart]);

  const initializeCart = useCallback(async () => {
    try {
      setCart({
        userId: selectedUser?.id || '',
        cartItems: [],
        currency: session?.defaultCurrency,
      });
    } catch (error) {
      console.error('Error creating cart:', error);
    }
  }, [selectedUser, session?.defaultCurrency]);

  // Update cart userId when selectedUser changes
  useEffect(() => {
    setCart((prevCart) => ({
      ...prevCart,
      userId: selectedUser?.id || '',
      locationId: selectedUser?.locationId || prevCart.locationId,
    }));
  }, [selectedUser]);

  // Track previous user prop to detect when it changes from defined to undefined
  const prevUserRef = React.useRef<User | undefined>(user);

  useEffect(() => {
    if (retryFromCartId) {
      prevUserRef.current = user;
      return;
    }

    // Only reset if user prop changed FROM a defined value TO undefined
    // Don't reset if user prop was already undefined (allows internal user selection)
    if (!user && prevUserRef.current) {
      setSelectedUser(undefined);
      setCurrentStep(PaymentStep.SelectUser);
      initializeCart();
      prevUserRef.current = user;
      return;
    }

    // If user prop changes to a different user, update selectedUser and currentStep
    if (user && selectedUser?.id !== user.id) {
      initializeCart();
      setSelectedUser(user);

      // Determine the appropriate step based on itemType
      if (itemType && selectableItemTypes.includes(itemType)) {
        setCurrentStep(PaymentStep.SelectItems);
      } else {
        setCurrentStep(PaymentStep.SelectType);
      }
    }

    // Update ref to track current user prop value
    prevUserRef.current = user;
  }, [user, itemType, setCurrentStep, initializeCart, selectedUser?.id, retryFromCartId]);

  const { data: paymentMethods, isLoading: loadingPaymentMethods } = useQuery({
    queryKey: ['payment-methods', selectedUser?.id],
    queryFn: () => fetchPaymentMethods(selectedUser?.id || ''),
    enabled: !!selectedUser?.id,
  });

  const addToCart = useCallback(
    async (item: any) => {
      if (cart) {
        if (!item.type) return;

        let cartItemItem: any = null;
        let cartItemType: string | number | undefined = undefined;
        if (item.type === 'onlineTraining') {
          cartItemItem = await fetchClient({ id: item.id });
          cartItemType = 'ClientAssociation';
        } else if (item.type === 'group') {
          cartItemItem = await fetchGroup({ id: item.id });
          cartItemType = 'Group';
        } else if (item.type === 'package') {
          cartItemItem = await fetchPackage({ id: item.id });
          cartItemType = 'Fbm::Package';
        } else if (item.type === 'product') {
          cartItemItem = await fetchProduct({ id: item.id });
          cartItemType = 'Exercisecom::Product';
        } else if (item.type === 'plan') {
          cartItemItem = await fetchWorkoutPlan({ id: item.id });
          cartItemType = 'WorkoutPlan';
        } else if (item.type === 'staffSubscription') {
          cartItemItem = await fetchTrainerByTrainerId({ trainerId: item.id });
          cartItemType = 'TrainerAssociation';
        } else if (item.type === 'product-variant') {
          cartItemItem = await fetchProductVariant({ id: item.id });
          cartItemType = 'ProductVariant';
        }

        // Check if item already exists in cart
        const existingItem = cart.cartItems.find(
          (cartItem) =>
            cartItem.itemId === item.id &&
            cartItem.itemType === cartItemType &&
            cartItem.itemType !== 'Fbm::Package'
        );

        if (
          cartItemType === 'ClientAssociation' &&
          !session?.stripePlan
        ) {
          toast.error(
            'This item cannot be added to the cart because there is no default payment option. Please set a default payment option on Customize Platform > Client Settings.'
          );
          return;
        }

        if (existingItem) {
          let message = 'This item is already in your cart.';
          if (existingItem.itemType === 'Exercisecom::Product') {
            message += ' Please edit the quantity instead.';
          }
          toast.error(message);
          return;
        }

        try {
          if (!cartItemItem) {
            console.error('Failed to fetch item details:', item);
            return;
          }

          // Create default payment option if none exist
          if (
            !cartItemItem.paymentOptions ||
            cartItemItem.paymentOptions.length === 0
          ) {
            const defaultOption: PaymentOption =
              (cartItemType === 'ClientAssociation' ||
                cartItemType === 'TrainerAssociation') &&
              session?.paymentOptions?.[0]
                ? session.paymentOptions[0]
                : {
                    id: 'default',
                    type: 'purchase',
                    amount: 0,
                    currency: session?.defaultCurrency || 'usd',
                    name: 'One Time Purchase',
                    location: null,
                  };
            cartItemItem.paymentOptions = [defaultOption];
          } else {
            cartItemItem.paymentOptions = cartItemItem.paymentOptions.map(
              (option: PaymentOption) => ({
                ...option,
                currency: option.currency || session?.defaultCurrency || 'usd',
              })
            );
          }

          const selectedPaymentOption = {
            ...cartItemItem.paymentOptions[0],
            currency:
              cartItemItem.paymentOptions[0].currency ||
              session?.defaultCurrency ||
              'usd',
          };

          const packageDefaults =
            cartItemType === 'Fbm::Package'
              ? getPackageDefaultDates(cartItemItem)
              : null;

          const newCartItem = {
            quantity: 1,
            userId: selectedUser?.id,
            item: cartItemItem,
            itemId: item.id,
            itemType: cartItemType,
            disableFee: undefined,
            chargedById: currentUser?.id,
            paymentOption: selectedPaymentOption,
            activationDate: packageDefaults?.activationDate,
            expiresOn: packageDefaults?.expiresOn ?? undefined,
          };

          const cartItems = [...(cart.cartItems || []), newCartItem];

          if (
            cartItemItem.feeableId &&
            cartItemItem.feeableType === 'Exercisecom::Product'
          ) {
            const feeableItem = await fetchProduct({
              id: cartItemItem.feeableId,
            });

            cartItems[cartItems.length - 1].disableFee = true;

            const feeablePaymentOption = {
              ...feeableItem.paymentOptions[0],
              currency:
                feeableItem.paymentOptions[0]?.currency ||
                session?.defaultCurrency ||
                'usd',
            };

            cartItems.push({
              quantity: 1,
              userId: selectedUser?.id,
              item: {
                ...feeableItem,
                paymentOption: feeablePaymentOption,
                item: feeableItem,
              },
              itemId: cartItemItem.feeableId,
              itemType: cartItemItem.feeableType,
              paymentOption: { ...feeablePaymentOption },
              disableFee: true,
            });
          }

          setCart({
            ...cart,
            currency: cart.currency || session?.defaultCurrency || 'usd',
            cartItems: cartItems,
          });
        } catch (error) {
          console.error('Error adding item to cart:', error);
        }
      }
    },
    [
      cart,
      session?.stripePlan,
      selectedUser?.id,
      currentUser?.id,
      session.paymentOptions,
      session?.defaultCurrency,
    ]
  );

  const setSelectedPaymentMethod = useCallback(
    (paymentMethod: string | PaymentMethod) => {
      setCart((prevCart) => ({
        ...prevCart,
        paymentMethod,
        checkNumber: isCheckPaymentMethod(paymentMethod)
          ? prevCart.checkNumber
          : undefined,
      }));
    },
    []
  );

  const setCheckNumber = useCallback((checkNumber: string) => {
    setCart((prevCart) => ({ ...prevCart, checkNumber }));
  }, []);

  const removeFromCart = useCallback(
    async (item: CartItem, index?: number) => {
      console.log('REMOVING FROM CART', item, index);
      if (cart) {
        try {
          if (index || index === 0) {
            setCart({
              ...cart,
              cartItems: cart.cartItems.filter((_, i) => i !== index),
            });
          } else {
            setCart({
              ...cart,
              cartItems: cart.cartItems.filter(
                (cartItem) =>
                  cartItem.itemId !== item.id && cartItem.itemId !== item.itemId
              ),
            });
          }
        } catch (error) {
          console.error('Error removing item from cart:', error);
        }
      }
    },
    [cart]
  );

  const updateLocationId = useCallback((locationId: Identifier) => {
    setCart((prevCart) => ({ ...prevCart, locationId: locationId as string }));
  }, []);

  const toggleAutomaticTax = useCallback(() => {
    setCart((prevCart) => ({
      ...prevCart,
      automaticTax: !prevCart.automaticTax,
    }));
  }, []);

  const handleEditItem = useCallback(
    (item: CartItem) => {
      if (item.paymentOption) {
        setItemToEdit(item);
        setCurrentStep(PaymentStep.EditItem);
      }
    },
    [setCurrentStep]
  );

  const handleSaveItemSettings = useCallback(() => {
    setItemToEdit(null);
    setCurrentStep(PaymentStep.SelectItems);
  }, [setCurrentStep]);

  const updateCartItem = useCallback(
    (updatedItem: CartItem) => {
      if (cart) {
        const updatedCartItems = cart.cartItems.map((item) =>
          item.itemId === updatedItem.itemId &&
          item.itemType === updatedItem.itemType
            ? updatedItem
            : item
        );
        setCart({ ...cart, cartItems: updatedCartItems });
      }
    },
    [cart]
  );

  const value = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      nextStep,
      prevStep,
      goToSelectUser,
      selectableItemTypes,
      selectedItemType,
      setSelectedItemType,
      searchValue,
      setSearchValue,
      debouncedSearchValue,
      cart,
      setCart,
      user: selectedUser,
      selectedUser,
      itemToEdit,
      setItemToEdit,
      savedCart,
      setSavedCart,
      cartError,
      setCartError,
      reset,
      resetSelectedItemType,
      fetchCartIsFetching,
      fetchCartIsLoading,
      fetchCartError,

      setSelectedUser,
      setSelectedPaymentMethod,
      setCheckNumber,
      paymentMethods,
      loadingPaymentMethods,
      addToCart,
      removeFromCart,
      handleEditItem,
      handleSaveItemSettings,
      updateCartItem,
      PaymentStep,

      filterParams,
      resetFilterParams,
      params,
      updateParam,
      updateFilterParam,
      getFilterParamValue,
      updateLocationId,
      toggleAutomaticTax,
      isRetryHydrating,
    }),
    [
      currentStep,
      nextStep,
      prevStep,
      goToSelectUser,
      selectedItemType,
      setSelectedItemType,
      searchValue,
      debouncedSearchValue,
      cart,
      itemToEdit,
      savedCart,
      cartError,
      reset,
      resetSelectedItemType,
      fetchCartIsFetching,
      fetchCartIsLoading,
      fetchCartError,
      selectedUser,
      setSelectedUser,
      setSelectedPaymentMethod,
      setCheckNumber,
      paymentMethods,
      loadingPaymentMethods,
      addToCart,
      removeFromCart,
      updateCartItem,
      filterParams,
      resetFilterParams,
      params,
      updateParam,
      updateFilterParam,
      getFilterParamValue,
      handleEditItem,
      handleSaveItemSettings,
      setCurrentStep,
      updateLocationId,
      toggleAutomaticTax,
      isRetryHydrating,
    ]
  );

  return (
    <PaymentDialogContext.Provider value={value}>
      {children}
    </PaymentDialogContext.Provider>
  );
};

export { PaymentDialogProvider, usePaymentDialogContext };
